import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

const BASE_QUERY_KEY = 'CustomPageStyle/status';

export interface CustomPageStyleStatusResponse {
	hasCustomization: boolean;
	error?: string;
}

/**
 * Fetches the account-level custom page style status for a target surface.
 *
 * The backend owns the canonical renderability rules: a style only counts as a customization when it
 * has meaningful content and is enabled for the requested target page type. Keeping that policy on
 * the API side prevents editor UI from drifting away from public-page rendering behavior.
 */
export const useCustomPageStyleByType = ({
	type,
	targetPageType,
	enabled = true,
	impersonationContext,
}: {
	type: Api.CustomPageType;
	targetPageType: Api.CustomPageType;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) => {
	const userSession = useUserSession();

	return useQuery<CustomPageStyleStatusResponse>({
		enabled,
		queryFn: async (): Promise<CustomPageStyleStatusResponse> => {
			try {
				const result = await userSession.webServiceHelper.callAsync<CustomPageStyleStatusResponse>(
					Api.ImpersonationBroker.composeApiUrl({
						impersonationContext,
						urlPath: `CustomPageStyle/${type}/status/${targetPageType}`,
					}),
					'GET'
				);
				// Normalize defensively: a malformed payload (null/undefined or a missing `hasCustomization`)
				// must never be read as a customization. Only an explicit `true` counts as enabled, so the
				// editor toggle stays aligned with the backend's canonical renderability rules even when the
				// API shape drifts.
				return { hasCustomization: result?.hasCustomization === true };
			} catch (networkError) {
				const apiError = Api.asApiError(networkError);
				if (apiError.systemCode === 404) {
					return { hasCustomization: false, error: 'No custom styles found' };
				}
				console.error('Network error fetching custom page style status:', networkError);
				return { hasCustomization: false, error: 'Network error occurred' };
			}
		},
		queryKey: [BASE_QUERY_KEY, type, targetPageType, impersonationContext],
		retry: (failureCount, error) => {
			// Only retry on network errors, not on 4xx client errors
			return failureCount < 2 && !(error as any)?.message?.includes('HTTP 4');
		},
	});
};
